
import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getArticleById } from '../data/newsData';
import Button from '../components/Button';
import VideoPlayer from '../components/VideoPlayer';
import { ArrowLeft, ExternalLink, Share2, Heart, Eye } from 'lucide-react';

const NewsDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : null;
  const article = articleId ? getArticleById(articleId) : null;
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center fade-in-up">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-xl mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/news">
            <Button variant="primary">Back to News</Button>
          </Link>
        </div>
      </div>
    );
  }

  // If article has external URL, redirect to it
  if (article.externalUrl) {
    window.location.href = article.externalUrl;
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center fade-in-up">
          <p className="text-xl">Redirecting to external article...</p>
        </div>
      </div>
    );
  }

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0 bg-black/30" 
        style={{
          backgroundImage: "url('/lovable-uploads/f2c064ce-cceb-4826-9ff6-2967b477e588.png')",
          backgroundSize: "100% auto",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "scroll"
        }}
      />
      
      <div className="relative z-10">
        {/* Header */}
        <div className={`container-custom py-8 ${isLoaded ? 'fade-in-left' : 'opacity-0'}`}>
          <Link to="/news" className="inline-flex items-center text-white hover:text-omoduduwa-terracotta mb-6 transition-all duration-300 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
            Back to News
          </Link>
        </div>

        {/* Article Content */}
        <article className="container-custom pb-16">
          <div className={`bg-black/50 backdrop-blur-sm rounded-xl overflow-hidden shadow-2xl ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            {/* Hero Image */}
            <div className="relative overflow-hidden">
              <img 
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover cursor-pointer transition-transform duration-500 hover:scale-105"
                onClick={() => setSelectedImage(article.image)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </div>
            
            <div className="p-8 md:p-12">
              {/* Article Meta */}
              <div className={`flex items-center justify-between mb-6 ${isLoaded ? 'fade-in-right' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-300 bg-black/60 px-3 py-1 rounded-md">
                    {article.date}
                  </span>
                  <span className="text-xs bg-omoduduwa-terracotta text-white px-3 py-1 rounded-full font-medium">
                    {article.category}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={shareArticle}
                    className="p-2 rounded-full bg-black/40 text-white hover:bg-omoduduwa-terracotta transition-colors duration-300"
                    title="Share article"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button className="p-2 rounded-full bg-black/40 text-white hover:text-red-400 transition-colors duration-300">
                    <Heart className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Article Title */}
              <h1 className={`text-3xl md:text-4xl font-bold mb-6 text-white ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
                {article.title}
              </h1>
              
              {/* Video Player if available */}
              {article.videoUrl && (
                <div className={`mb-8 ${isLoaded ? 'scale-in' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
                  <VideoPlayer
                    videoUrl={article.videoUrl}
                    title={article.title}
                    className="aspect-video rounded-lg overflow-hidden"
                  />
                </div>
              )}
              
              {/* Article Content */}
              <div 
                className={`prose prose-invert prose-orange max-w-none text-white/90 prose-headings:text-white prose-a:text-omoduduwa-terracotta hover:prose-a:text-omoduduwa-terracotta/80 ${isLoaded ? 'fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: '1s' }}
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
              
              {/* Image Gallery */}
              {article.gallery && article.gallery.length > 1 && (
                <div className={`mt-8 ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '1.2s' }}>
                  <h3 className="text-xl font-bold mb-4 text-white">Photo Gallery</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {article.gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Gallery image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg cursor-pointer transition-transform duration-300 hover:scale-105 shadow-lg"
                        onClick={() => setSelectedImage(image)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Article Stats */}
              <div className={`mt-8 pt-6 border-t border-white/20 flex items-center gap-6 text-white/70 ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '1.4s' }}>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">1,234 views</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm">89 likes</span>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img 
              src={selectedImage}
              alt="Enlarged view"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-omoduduwa-terracotta text-2xl transition-colors duration-300"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetailPage;
